import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { DateTime } from 'luxon';
import { useTenant } from '@/hooks/use-tenant';
import { tenantHomeQueryOptions } from '@/hooks/use-tenant-home';
import type { TenantHomeResponseDto } from '@/generated/korza-api-client/types.gen';

type Points = TenantHomeResponseDto['revenueChart']['points'];

export type TodaySoFarRow = {
  /** Hours since 00:00 (0–24; fractional for "now"). */
  x: number;
  today: number | null;
  yesterday: number | null;
};

/** Revenue of the buckets that start before hour `x` of their day. */
function cumulativeUntil(points: Points, x: number): number {
  return points.reduce((sum, point) => {
    const start = DateTime.fromISO(point.date);
    const hours = start.hour + start.minute / 60;
    return hours < x ? sum + point.grossRevenueUsd : sum;
  }, 0);
}

/**
 * Cumulative revenue by hour (HOME-05): today up to now (its last point is
 * today's total at the current time), yesterday for the whole day.
 */
export function todaySoFarRows(
  today: Points,
  todayTotal: number,
  yesterday: Points | undefined,
  now: DateTime
): TodaySoFarRow[] {
  const nowX = now.hour + now.minute / 60;
  const rows: TodaySoFarRow[] = [];
  for (let x = 0; x <= 24; x++) {
    rows.push({
      x,
      today: x < nowX ? cumulativeUntil(today, x) : null,
      yesterday: yesterday ? cumulativeUntil(yesterday, x) : null,
    });
  }
  const atNow = rows.find((row) => row.x === nowX);
  if (atNow) atNow.today = todayTotal;
  else {
    rows.push({ x: nowX, today: todayTotal, yesterday: null });
    rows.sort((a, b) => a.x - b.x);
  }
  return rows;
}

/**
 * "Hoje até agora" of the Início (HOME-05), independent of the page period:
 * `/analytics/home` by hour for today 00:00→now, yesterday 00:00→(now − 24h)
 * (the comparison) and the whole of yesterday (the dashed line and "fechou
 * em"). `now` is fixed when the card mounts.
 */
export function useTodaySoFar() {
  const tenant = useTenant();
  const [now] = useState(() => DateTime.now());

  const startOfToday = now.startOf('day');
  const startOfYesterday = startOfToday.minus({ days: 1 });
  const query = (from: DateTime, to: DateTime) =>
    tenantHomeQueryOptions(tenant.slug, {
      fromDate: from.toISO() ?? '',
      toDate: to.toISO() ?? '',
      granularity: 'hour',
    });

  const today = useQuery(query(startOfToday, now));
  const yesterdayUntilNow = useQuery(
    query(startOfYesterday, now.minus({ hours: 24 }))
  );
  const yesterday = useQuery(
    query(startOfYesterday, startOfYesterday.endOf('day'))
  );

  return { now, today, yesterdayUntilNow, yesterday };
}
